/* hs-eslint ignored failing-rules */

import { useState } from 'react';
import { useMutation, useDataFetchingClient } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import H2 from 'UIComponents/elements/headings/H2';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIModal from 'UIComponents/dialog/UIModal';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import Small from 'UIComponents/elements/Small';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import BusinessUnitSelect from 'ui-business-units-lib/components/BusinessUnitSelect';
import WithBusinessUnitsDataFetching from 'ui-business-units-lib/utils/WithBusinessUnitsDataFetching';
import { BusinessUnitsRebrandMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandMessage';
import { BusinessUnitsRebrandJSXMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandJSXMessage';
import DocButton from 'automation-ui-usage-tracking/components/DocButton';
import { SupportLinkKeys } from 'automation-ui-usage-tracking/SupportLinks';
import { AUTOMATION_PLATFORM_FLOW_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import { AutomationPlatformFlowProperties } from 'automation-ui-management/constants/CrmWorkflowProperties';
import { UPDATE_CRM_OBJECTS_BUSINESS_UNIT, CRM_OBJECT_SEARCH_FIELD_NAME } from 'automation-ui-management/queries/crmObjectQueries';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BusinessUnitSelectorWithDataFetching = WithBusinessUnitsDataFetching(BusinessUnitSelect);
const AssignBusinessUnitModal = ({
  crmObject,
  onClose,
  onSuccess = () => {}
}) => {
  const client = useDataFetchingClient();
  const workflowName = getPropertyValue(crmObject, AutomationPlatformFlowProperties.NAME);
  const initialBusinessUnitValue = getPropertyValue(crmObject, AutomationPlatformFlowProperties.BUSINESS_UNIT);
  const initialBusinessUnitId = initialBusinessUnitValue ? parseInt(initialBusinessUnitValue, 10) : undefined;
  const objectId = String(getPropertyValue(crmObject, AutomationPlatformFlowProperties.OBJECT_ID));
  const [businessUnitId, setBusinessUnitId] = useState(initialBusinessUnitId);
  const [error, setError] = useState(false);
  const [updateProperty] = useMutation(UPDATE_CRM_OBJECTS_BUSINESS_UNIT, {
    variables: {
      changes: {
        [objectId]: String(businessUnitId)
      },
      objectTypeId: AUTOMATION_PLATFORM_FLOW_ID
    },
    onCompleted: () => {
      setError(false);
      // Update internal cache so that the business unit will update right away on the index page
      listingLibObserver.updatePropertyValues({
        selectedIds: [parseInt(objectId, 10)],
        propertyValues: [{
          name: AutomationPlatformFlowProperties.BUSINESS_UNIT,
          value: String(businessUnitId)
        }]
      });
      // additional cache clearing needed for the details page
      client.cache.evict({
        fieldName: CRM_OBJECT_SEARCH_FIELD_NAME
      });
      onSuccess();
      onClose();
    },
    onError: () => {
      setError(true);
    }
  });
  return /*#__PURE__*/_jsxs(UIModal, {
    "aria-label": I18n.text('automation-ui-management.AssignBusinessUnitModal.assignBU.modalHeader'),
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.assignBU.modalHeader"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(View, {
        paddingBlockEnd: "300",
        as: "div",
        children: initialBusinessUnitId === undefined ? /*#__PURE__*/_jsx(BusinessUnitsRebrandJSXMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.assignBU.selectBusinessUnit_jsx",
          rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.assignBU.selectBusinessUnit_buv2_jsx",
          elements: {
            b: 'b'
          },
          options: {
            workflowName
          }
        }) : /*#__PURE__*/_jsx(BusinessUnitsRebrandJSXMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.changeBU.changeBusinessUnit_jsx",
          rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.changeBU.changeBusinessUnit_buv2_jsx",
          elements: {
            b: 'b'
          },
          options: {
            workflowName
          }
        })
      }), /*#__PURE__*/_jsx(UIForm, {
        children: /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
            message: "automation-ui-management.AssignBusinessUnitModal.label",
            rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.label_buv2"
          }),
          children: /*#__PURE__*/_jsx(BusinessUnitSelectorWithDataFetching, {
            buId: businessUnitId,
            onChange: newBU => {
              setBusinessUnitId(newBU);
            }
          })
        })
      }), /*#__PURE__*/_jsxs(UIAlert, {
        type: "warning",
        className: "m-y-4",
        titleText: /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.warning.header",
          rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.warning.header_buv2"
        }),
        children: [/*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.warning.body",
          rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.warning.body_buv2"
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          as: "div",
          children: /*#__PURE__*/_jsx(BusinessUnitsRebrandJSXMessage, {
            options: {
              businessUnitWorkflowsTips: SupportLinkKeys.BUSINESS_UNITS_IN_WORKFLOWS_TIPS
            },
            elements: {
              DocButton
            },
            message: "automation-ui-management.AssignBusinessUnitModal.warning.learnMore_jsx",
            rebrandMessage: "automation-ui-management.AssignBusinessUnitModal.warning.learnMore_buv2_jsx"
          })
        })]
      }), error && /*#__PURE__*/_jsx(Small, {
        use: "error",
        children: initialBusinessUnitId === undefined ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.assignBU.error"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.changeBU.error"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.changeBU.requiresChangeTooltip"
        }),
        disabled: businessUnitId !== initialBusinessUnitId || businessUnitId === undefined,
        placement: "bottom",
        children: /*#__PURE__*/_jsx(UIButton, {
          disabled: businessUnitId === initialBusinessUnitId,
          onClick: () => {
            updateProperty().catch(err => {
              reportError({
                error: err
              });
            });
          },
          use: "primary",
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.AssignBusinessUnitModal.assignBU.submitButton"
          })
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "secondary",
        type: "submit",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.AssignBusinessUnitModal.cancelButton"
        })
      })]
    })]
  });
};
export default AssignBusinessUnitModal;